// Vec2 - 2D vector math

export class Vec2 {
  constructor(x = 0, y = 0) {
    this.x = x;
    this.y = y;
  }

  copy() {
    return new Vec2(this.x, this.y);
  }

  set(x, y) {
    this.x = x;
    this.y = y;
    return this;
  }

  add(v) {
    return new Vec2(this.x + v.x, this.y + v.y);
  }

  sub(v) {
    return new Vec2(this.x - v.x, this.y - v.y);
  }

  mul(s) {
    return new Vec2(this.x * s, this.y * s);
  }

  div(s) {
    return new Vec2(this.x / s, this.y / s);
  }

  dot(v) {
    return this.x * v.x + this.y * v.y;
  }

  len() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  lenSq() {
    return this.x * this.x + this.y * this.y;
  }

  normalize() {
    const l = this.len();
    if (l === 0) return new Vec2(0, 0);
    return this.div(l);
  }

  dist(v) {
    return this.sub(v).len();
  }

  distSq(v) {
    return this.sub(v).lenSq();
  }

  angle() {
    return Math.atan2(this.y, this.x);
  }

  rotate(angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Vec2(
      this.x * cos - this.y * sin,
      this.x * sin + this.y * cos
    );
  }

  lerp(v, t) {
    return new Vec2(
      this.x + (v.x - this.x) * t,
      this.y + (v.y - this.y) * t
    );
  }

  clampLen(maxLen) {
    const l = this.len();
    if (l > maxLen) return this.normalize().mul(maxLen);
    return this.copy();
  }

  equals(v) {
    return Math.abs(this.x - v.x) < 0.0001 && Math.abs(this.y - v.y) < 0.0001;
  }

  static fromAngle(angle) {
    return new Vec2(Math.cos(angle), Math.sin(angle));
  }
}

// Collision utilities

export function circlesOverlap(ax, ay, ar, bx, by, br) {
  const dx = ax - bx;
  const dy = ay - by;
  const distSq = dx * dx + dy * dy;
  const radSum = ar + br;
  return distSq < radSum * radSum;
}

export function pointInRect(px, py, rx, ry, rw, rh) {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

export function rectOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

export function pointInCircle(px, py, cx, cy, cr) {
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy < cr * cr;
}

export function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

export function randRange(min, max) {
  return min + Math.random() * (max - min);
}

export function randInt(min, max) {
  return Math.floor(randRange(min, max + 1));
}

export function choose(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Seeded random for reproducible terrain
export function seededRandom(seed) {
  let s = seed;
  return function () {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

// Game constants

export const CONST = {
  // World
  WORLD_W: 2400,
  WORLD_H: 1600,
  TILE_SIZE: 32,

  // Player
  PLAYER_SPEED: 95,
  PLAYER_ACCEL: 800,
  PLAYER_FRICTION: 6,
  PLAYER_RADIUS: 12,
  PLAYER_MAX_HP: 100,
  PLAYER_BASE_DAMAGE: 15,
  PLAYER_ATTACK_RANGE: 28,
  PLAYER_ATTACK_COOLDOWN: 0.4,
  // Low passive regen — most sustain comes from the 'regen' card (1.85/copy,
  // see CARDS in ui.js), but a small base keeps the early waves playable.
  // Sim sweep (smart × 200): base 0 → 86% die on wave 4; 1.5 gives the
  // smoothest curve (median wave 8, ~30% reach wave 10) while the card still
  // matters (lifts regen to ~4/sec at a full stack).
  PLAYER_HP_REGEN_PER_SEC: 1.5,
  // 'bloodFog' relic: vignette begins to redden below this HP fraction
  // and saturates at zero. Tuned so it picks up about the same moment
  // a single basic-guard hit would be the difference between safe and
  // dangerous, not so early it becomes ambient noise.
  BLOOD_FOG_THRESHOLD: 0.35,
  BLOOD_FOG_PEAK_ALPHA: 0.55,
  PLAYER_DASH_SPEED: 460,
  PLAYER_DASH_DURATION: 0.18,
  PLAYER_DASH_COOLDOWN: 1.0,
  PLAYER_DASH_IFRAME_DURATION: 0.22,
  // Hard ceiling on dash i-frames, as a fraction of the *current* dash
  // cooldown. Without it the phantom card's 1.0s override exactly matched
  // PLAYER_DASH_COOLDOWN — 100% invulnerability uptime for anyone spamming
  // dash — and a single dashCooldown stack (cd ×0.75, iframe +0.05) pushed
  // it to 140%. Clamping to 0.6 guarantees at least 40% of every dash cycle
  // is vulnerable no matter how the build stacks.
  DASH_IFRAME_MAX_CD_FRACTION: 0.6,
  // Heat after Rebirth: every rebirth in a run makes guards faster by this
  // share of their normal speed, capped at HEAT_GUARD_SPEED_CAP of the pace
  // of a card-less robber dashing on every cooldown (dashPace in caravan.js,
  // ≈ 161 px/s). From heat 1 an elite outruns a walking robber; no guard
  // ever outruns a dashing one.
  HEAT_GUARD_SPEED_STEP: 0.2,
  HEAT_GUARD_SPEED_CAP: 0.9,
  // Knockback impulse (lancer charge). Additive on top of normal movement:
  // the player keeps control, they just get shoved.
  PLAYER_KNOCKBACK_DURATION: 0.25,
  PLAYER_KNOCKBACK_DECAY: 6,

  // Super strike (Q / touch button): a meter filled by melee damage dealt
  // (the super's own damage doesn't refill it), kept for the whole run.
  // Full meter → a ring blast around the robber: SUPER_DAMAGE_MUL × the
  // current hit (cards, rage, crit, armorBreak apply) to every guard and
  // caravan within SUPER_RADIUS, guards shoved away. 600 ≈ a bit more than
  // the median real player deals in a wave (~490, telemetry 2026-10).
  SUPER_CHARGE_MAX: 600,
  SUPER_DAMAGE_MUL: 5,
  SUPER_RADIUS: 120,
  // Guard shove: same exponential decay as the player knockback; the guard
  // is staggered (no AI, no attacks) while it flies.
  SUPER_KNOCKBACK: 520,
  GUARD_KNOCKBACK_DURATION: 0.3,
  GUARD_KNOCKBACK_DECAY: 6,

  // Horse (rare guard drop): while mounted the robber is faster and every
  // hit (bombs and burn included) lands on the horse's own HP bar first;
  // the overflow of the killing hit reaches the robber. The bar never
  // refills — at 0 the horse is gone. HP = HORSE_HP_FRAC × the robber's
  // max HP at mounting. Stays across waves; Rebirth makes a new Player.
  HORSE_SPEED_MUL: 1.4,
  HORSE_HP_FRAC: 0.5,
  HORSE_MIN_WAVE: 5,

  // Guards - basic (sword)
  GUARD_SPEED: 80,
  GUARD_DETECTION_RANGE: 120,
  GUARD_CHASE_RANGE: 200,
  GUARD_ATTACK_RANGE: 20,
  GUARD_ATTACK_COOLDOWN: 0.8,
  GUARD_RADIUS: 10,
  GUARD_BASE_HP: 40,
  GUARD_BASE_DAMAGE: 8,

  // Guards - armored
  ARMORED_GUARD_SPEED: 55,
  ARMORED_GUARD_HP: 90,
  ARMORED_GUARD_DAMAGE: 12,
  ARMORED_GUARD_DETECTION_RANGE: 100,
  ARMORED_GUARD_ARMOR: 30,

  // Guards - archer
  ARCHER_GUARD_SPEED: 65,
  ARCHER_GUARD_HP: 30,
  ARCHER_GUARD_DAMAGE: 6,
  ARCHER_GUARD_DETECTION_RANGE: 180,
  ARCHER_GUARD_ATTACK_RANGE: 150,
  ARCHER_GUARD_ATTACK_COOLDOWN: 1.5,
  ARCHER_MIN_WAVE: 6, // archers' debut wave, 25% of an escort from then on (1.7 rebalance; was 7)
  ARCHER_PREFERRED_DIST: 130,

  // Guards - lancer (v1.6.0). Melee specialist that answers dodge-spam
  // builds: it closes distance with a telegraphed charge instead of
  // walking into swing range. The charge respects i-frames (it's a hit,
  // not a field), so the counterplay is timing the dash — not holding it.
  LANCER_HP: 55,
  LANCER_DAMAGE: 10,
  LANCER_SPEED: 78,
  LANCER_DETECTION_RANGE: 150,
  LANCER_ATTACK_RANGE: 26,
  LANCER_ATTACK_COOLDOWN: 1.0,
  LANCER_MIN_WAVE: 9, // 1.7 rebalance (was 12)
  LANCER_SHARE_START: 0.12, // share of an escort on the debut wave, then +1.5%/wave to 20%
  LANCER_CHARGE_MIN_DIST: 70,
  // Must stay below GUARD_CHASE_RANGE: the charge only triggers from
  // CHASE, and the state machine drops to RETURN past the chase range in
  // the same tick, so anything above it is unreachable and reads as a
  // tuning knob that does nothing. Pinned by tests/test-difficulty.js.
  LANCER_CHARGE_MAX_DIST: 190,
  LANCER_CHARGE_WINDUP: 0.45,
  LANCER_CHARGE_SPEED: 420,
  LANCER_CHARGE_DURATION: 0.38,
  LANCER_CHARGE_RECOVER: 0.5,
  LANCER_CHARGE_COOLDOWN: 3.2,
  LANCER_CHARGE_DAMAGE_MUL: 1.8,
  LANCER_CHARGE_KNOCKBACK: 260,

  // Guards - bombardier (v1.6.0). Lobs a fused bomb at a predicted
  // landing spot. The blast is a ground field, not a discrete hit, so it
  // bypasses i-frames exactly like the fire-arrow burn does — the answer
  // is to walk out of the marked circle, not to blink through it.
  BOMBARDIER_HP: 34,
  BOMBARDIER_DAMAGE: 14,
  BOMBARDIER_SPEED: 60,
  BOMBARDIER_DETECTION_RANGE: 200,
  BOMBARDIER_ATTACK_RANGE: 190,
  BOMBARDIER_ATTACK_COOLDOWN: 3.0,
  BOMBARDIER_PREFERRED_DIST: 150,
  BOMBARDIER_MIN_WAVE: 11, // 1.7 rebalance (was 16)
  BOMBARDIER_SHARE_START: 0.10, // share on the debut wave, then +1%/wave to 12%
  BOMB_FUSE_SEC: 1.25,
  BOMB_RADIUS: 46,
  BOMB_LEAD_FACTOR: 0.55,
  BOMB_SCATTER: 14,
  BOMB_BURN_DURATION: 3,
  BOMB_BURN_DPS: 3,

  // Projectiles
  PROJECTILE_SPEED: 200,
  PROJECTILE_RADIUS: 4,
  PROJECTILE_LIFETIME: 3,
  // Fire arrows (archers from FIRE_ARROW_MIN_WAVE+) apply a burn DoT on
  // hit. Refreshing the burn extends the duration; per-tick damage is
  // additive so the player can't sit in fire forever for free.
  FIRE_ARROW_MIN_WAVE: 8, // 1.7 rebalance (was 10)
  FIRE_ARROW_BURN_DURATION: 4,
  FIRE_ARROW_BURN_DPS: 2,
  // Sticky arrows (v1.6.0): low direct damage, but they glue the player
  // down — dash is locked out and movement speed is cut for the duration.
  // Rolled before the fire variant so an archer fires one or the other.
  STICKY_ARROW_MIN_WAVE: 12, // 1.7 rebalance (was 14)
  STICKY_ARROW_CHANCE_BASE: 0.18,
  STICKY_ARROW_CHANCE_PER_WAVE: 0.015,
  STICKY_ARROW_CHANCE_CAP: 0.4,
  STICKY_DURATION: 2.2,
  STICKY_SPEED_MUL: 0.5,
  STICKY_DAMAGE_MUL: 0.5,

  // Late-wave head-count budget. Raw scaling (+1 guard per caravan per 3
  // waves, and caravan count itself grows) reached 600+ guards by wave 70:
  // a frame-rate problem, not a difficulty one — a 25-deep marching column
  // never actually reaches the player. Spawns are capped here and the
  // surplus is converted into elite tiers instead (see planWaveGuards and
  // ELITE_TIERS in caravan.js): fewer bodies, each one much harder.
  WAVE_GUARD_CAP: 85,
  // Elite tiers have two independent drivers (see elitePressure):
  //   - compression, when the cap above starts discarding bodies (~w45);
  //   - wave age, from ELITE_MIN_WAVE, so elites are content the player
  //     meets on the way up rather than only after the cap bites.
  // ELITE_WAVE_DEBUT is the step the debut wave itself gets, so the tier
  // actually shows up on wave ELITE_MIN_WAVE instead of being a coin flip;
  // ELITE_WAVE_RAMP is the shallow per-wave growth after it. Tuned so each
  // tier arrives on its own rather than all at once: veterans at w20,
  // champions around w40, executioners around w80 — and compression takes
  // over from there.
  ELITE_MIN_WAVE: 20,
  ELITE_WAVE_DEBUT: 0.15,
  ELITE_WAVE_RAMP: 0.045,

  // Boss
  BOSS_HP_MULTIPLIER: 3,
  // Loot multiplier grows per boss cycle: base + floor(wave/5) - 1.
  // wave5: ×1.5, wave10: ×2.5, wave15: ×3.5. Keeps early bosses from
  // dumping unspendable gold while late bosses still scale.
  BOSS_LOOT_MULTIPLIER_BASE: 1.5,
  BOSS_GUARD_COUNT: 5,

  // Scoring
  FLAWLESS_WAVE_BONUS: 50,

  // Caravans
  CARAVAN_RADIUS: 18,
  DONKEY_SPEED: 40,
  WAGON_SPEED: 30,
  ROYAL_SPEED: 20,

  // Loot
  LOOT_RADIUS: 6,
  LOOT_MAGNET_RANGE: 40,
  LOOT_MAGNET_SPEED: 200,

  // Guard drops — "ништяки" that fall from killed guards (see pickups.js).
  // Themed after what caravan escorts carry in other games: Fallout chems
  // map to temporary buffs (Psycho→rage, Turbo/Jet→frenzy, Med-X/Buffout→
  // damage-resist), bottle caps / M&B denars → coin purses, and Mount &
  // Blade / Battle Brothers trade goods → the rare spice jackpot.
  GUARD_DROP_CHANCE: 0.22,
  // Horse weight in the drop table: ≈ 8% of drops at full HP (the medkit
  // weight grows as HP falls, so a hurt robber sees fewer horses).
  PICKUP_HORSE_WEIGHT: 7,
  PICKUP_RADIUS: 7,
  // Almost no pull — collection happens at PICKUP_RADIUS + PLAYER_RADIUS
  // (≈19), so a 20u magnet means the player must walk right onto a drop to
  // grab it (intentionally tighter than coins' LOOT_MAGNET_RANGE).
  PICKUP_MAGNET_RANGE: 20,
  PICKUP_MAGNET_SPEED: 220,
  PICKUP_HEAL_AMOUNT: 20,
  PICKUP_COIN_MIN: 1,
  PICKUP_COIN_MAX: 5,
  // Spice jackpot: a rare big gold drop that scales lightly with wave so
  // it stays a treat late game without flooding the economy.
  PICKUP_SPICE_GOLD_BASE: 30,
  PICKUP_SPICE_GOLD_PER_WAVE: 3,
  // Temporary buffs (Fallout chem analogues). Durations in seconds.
  BUFF_RAGE_DURATION: 8,       // Боевой клич — Psycho (+damage)
  BUFF_RAGE_DAMAGE_MUL: 1.5,
  BUFF_FRENZY_DURATION: 8,     // Быстрая рука — Turbo/Jet (faster swings)
  BUFF_FRENZY_CD_MUL: 0.6,     // attack cooldown ×0.6 (−40%)
  BUFF_GUARD_DURATION: 5,      // Кураж — Med-X/Buffout (damage resistance)
  BUFF_GUARD_RESIST: 0.4,      // ignore 40% of incoming damage

  // UI
  HUD_HEIGHT: 40,

  // Colors
  COLOR_SAND: '#e8c872',
  COLOR_SAND_DARK: '#d4b45c',
  COLOR_SAND_LIGHT: '#f0d888',
  COLOR_ROAD: '#c4a24e',
  COLOR_ROAD_EDGE: '#b0903a',
  COLOR_BUSH: '#6b8e3a',
  COLOR_ROCK: '#8a8278',
  COLOR_GOLD: '#ffd700',
  COLOR_HP_BAR: '#e74c3c',
  COLOR_HP_BG: '#333',
  COLOR_PLAYER: '#c0392b',
  COLOR_GUARD: '#2c3e50',
  COLOR_CARAVAN: '#8b6914',
};
